import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../config";

export default function EditPoolCareBusinessFranchise() {
  const { id } = useParams();
  const navigate = useNavigate();


  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [listingType, setListingType] = useState("business");

  // ✅ MUST match POST form fields
  const [form, setForm] = useState({
    business_title: "",
    business_description: "",
    yearly_revenue: "",
    clients_count: "",
    total_client_value: "",

    franchise_type: "new",
    franchise_brand: "",
    territory_name: "",
    territory_type: "exclusive",
    number_of_clients: "",
    avg_job_price: "",

    vehicle_included: "0",
    vehicle_details: "",
    equipment_included: "0",
    equipment_details: "",

    work_guarantee: "0",
    work_guarantee_amount: "",
    work_guarantee_period: "",

    suburb: "",
    state: "",
    postcode: "",

    price: "",
    enquiry_only: "0",
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const n = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const num = Number(v);
    return Number.isFinite(num) ? num : 0;
  };

  /* =========================
     LOAD LISTING
  ========================= */
  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/poolcare/business-franchise/${id}`,
          { headers }
        );

        if (!res.data?.success || !res.data.listing) {
          setError("Listing not found.");
          setLoading(false);
          return;
        }

        const l = res.data.listing;

        setListingType(l.listing_type || "business");

        setForm({
          business_title: l.business_title || "",
          business_description: l.business_description || "",
          yearly_revenue: l.yearly_revenue ?? "",
          clients_count: l.clients_count ?? "",
          total_client_value: l.total_client_value ?? "",

          franchise_type: l.franchise_type || "new",
          franchise_brand: l.franchise_brand || "",
          territory_name: l.territory_name || "",
          territory_type: l.territory_type || "exclusive",
          number_of_clients: l.number_of_clients ?? "",
          avg_job_price: l.avg_job_price ?? "",

          vehicle_included: String(l.vehicle_included ?? 0),
          vehicle_details: l.vehicle_details || "",
          equipment_included: String(l.equipment_included ?? 0),
          equipment_details: l.equipment_details || "",

          work_guarantee: String(l.work_guarantee ?? 0),
          work_guarantee_amount: l.work_guarantee_amount ?? "",
          work_guarantee_period: l.work_guarantee_period || "",

          suburb: l.suburb || "",
          state: l.state || "",
          postcode: l.postcode || "",

          price: l.price ?? "",
          enquiry_only: String(l.enquiry_only ?? 0),
        });
      } catch (err) {
        console.error("LOAD ERROR:", err);
        setError(
          err?.response?.data?.message ||
            "Failed to load listing. Check backend route + auth."
        );
      }

      setLoading(false);
    }

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [id]);

  /* =========================
     SAVE CHANGES
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = {
        listing_type: listingType,

        business_title: form.business_title,
        business_description: form.business_description,
        yearly_revenue: n(form.yearly_revenue),
        clients_count: n(form.clients_count),
        total_client_value: n(form.total_client_value),

        franchise_type: form.franchise_type,
        franchise_brand: form.franchise_brand,
        territory_name: form.territory_name,
        territory_type: form.territory_type,
        number_of_clients: n(form.number_of_clients),
        avg_job_price: n(form.avg_job_price),

        vehicle_included: n(form.vehicle_included),
        vehicle_details: form.vehicle_details,
        equipment_included: n(form.equipment_included),
        equipment_details: form.equipment_details,

        work_guarantee: n(form.work_guarantee),
        work_guarantee_amount: n(form.work_guarantee_amount),
        work_guarantee_period: form.work_guarantee_period,

        price: n(form.price),
        suburb: form.suburb,
        state: form.state,
        postcode: form.postcode,
        enquiry_only: n(form.enquiry_only),
      };

      const res = await axios.put(
        `${API_BASE_URL}/api/poolcare/business-franchise/update/${id}`,
        payload,
        { headers }
      );

      if (res.data?.success) {
        navigate("/my-listings");
        return;
      }

      setError(res.data?.message || "Save failed.");
    } catch (err) {
      console.error("SAVE ERROR:", err);
      setError(err?.response?.data?.message || "Server error saving changes.");
    }

    setSaving(false);
  };

  /* =========================
     STYLES
  ========================= */
  const container = {
    maxWidth: "760px",
    margin: "120px auto 40px",
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  const section = {
    background: "#e8f5e9",
    padding: "10px",
    borderRadius: "6px",
    marginTop: "20px",
    fontWeight: "bold",
    color: "#1b5e20",
  };

  const input = {
    width: "100%",
    padding: "10px",
    marginTop: "8px",
    marginBottom: "12px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "1rem",
  };

  const row = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
  };

  const errorBox = {
    background: "#ffebee",
    border: "1px solid #ffcdd2",
    color: "#b71c1c",
    padding: "10px 12px",
    borderRadius: 8,
    marginBottom: 12,
    fontWeight: 600,
  };

  if (loading) return <p style={{ textAlign: "center" }}>Loading…</p>;

  return (
    <div style={container}>
      <h2>Edit Pool Care Business Listing</h2>

      {error && <div style={errorBox}>{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Listing Type */}
        <div style={section}>Listing Type</div>
        <select
          style={input}
          value={listingType}
          onChange={(e) => setListingType(e.target.value)}
        >
          <option value="business">Business</option>
          
        </select>

        {/* Business */}
        {listingType === "business" && (
          <>
            <div style={section}>Business Information</div>

            <input
              style={input}
              name="business_title"
              placeholder="Business Title"
              value={form.business_title}
              onChange={handleChange}
            />

            <textarea
              style={{ ...input, minHeight: 110 }}
              name="business_description"
              placeholder="Business Description"
              value={form.business_description}
              onChange={handleChange}
            />

            <div style={row}>
              <input
                style={input}
                name="yearly_revenue"
                placeholder="Yearly Revenue"
                value={form.yearly_revenue}
                onChange={handleChange}
              />
              <input
                style={input}
                name="clients_count"
                placeholder="Clients Count"
                value={form.clients_count}
                onChange={handleChange}
              />
            </div>

            <input
              style={input}
              name="total_client_value"
              placeholder="Total Client Value"
              value={form.total_client_value}
              onChange={handleChange}
            />
          </>
        )}

        {/* Franchise */}
        {false && (
          <>
            <div style={section}>Franchise Information</div>

            {/* franchise_type (missing in your current UI) */}
            <select
              style={input}
              name="franchise_type"
              value={form.franchise_type}
              onChange={handleChange}
            >
              <option value="new">New Franchise</option>
              <option value="existing">Existing Franchise</option>
            </select>

            <input
              style={input}
              name="franchise_brand"
              placeholder="Franchise Brand"
              value={form.franchise_brand}
              onChange={handleChange}
            />

            <input
              style={input}
              name="territory_name"
              placeholder="Territory Name"
              value={form.territory_name}
              onChange={handleChange}
            />

            {/* territory_type (missing in your current UI) */}
            <select
              style={input}
              name="territory_type"
              value={form.territory_type}
              onChange={handleChange}
            >
              <option value="exclusive">Exclusive</option>
              <option value="non_exclusive">Non-Exclusive</option>
            </select>

            <div style={row}>
              <input
                style={input}
                name="number_of_clients"
                placeholder="Number of Clients"
                value={form.number_of_clients}
                onChange={handleChange}
              />
              <input
                style={input}
                name="avg_job_price"
                placeholder="Avg Job Price"
                value={form.avg_job_price}
                onChange={handleChange}
              />
            </div>
          </>
        )}

        {/* Assets Included */}
        <div style={section}>Assets Included</div>

        <label style={{ display: "block", marginTop: 6 }}>
          <input
            type="checkbox"
            checked={form.vehicle_included === "1"}
            onChange={(e) =>
              setForm({
                ...form,
                vehicle_included: e.target.checked ? "1" : "0",
              })
            }
          />{" "}
          Vehicle Included
        </label>

        {form.vehicle_included === "1" && (
          <textarea
            style={{ ...input, minHeight: 80 }}
            name="vehicle_details"
            placeholder="Vehicle Details"
            value={form.vehicle_details}
            onChange={handleChange}
          />
        )}

        <label style={{ display: "block", marginTop: 6 }}>
          <input
            type="checkbox"
            checked={form.equipment_included === "1"}
            onChange={(e) =>
              setForm({
                ...form,
                equipment_included: e.target.checked ? "1" : "0",
              })
            }
          />{" "}
          Equipment Included
        </label>

        {form.equipment_included === "1" && (
          <textarea
            style={{ ...input, minHeight: 80 }}
            name="equipment_details"
            placeholder="Equipment Details"
            value={form.equipment_details}
            onChange={handleChange}
          />
        )}

        {/* Work Guarantee */}
        <div style={section}>Work Guarantee</div>

        <label style={{ display: "block", marginTop: 6 }}>
          <input
            type="checkbox"
            checked={form.work_guarantee === "1"}
            onChange={(e) =>
              setForm({
                ...form,
                work_guarantee: e.target.checked ? "1" : "0",
              })
            }
          />{" "}
          Work Guarantee
        </label>

        {form.work_guarantee === "1" && (
          <div style={row}>
            <input
              style={input}
              name="work_guarantee_amount"
              placeholder="Guarantee Amount"
              value={form.work_guarantee_amount}
              onChange={handleChange}
            />
            <input
              style={input}
              name="work_guarantee_period"
              placeholder="Guarantee Period"
              value={form.work_guarantee_period}
              onChange={handleChange}
            />
          </div>
        )}

        {/* Location */}
        <div style={section}>Location</div>
        <div style={row}>
          <input
            style={input}
            name="suburb"
            placeholder="Suburb"
            value={form.suburb}
            onChange={handleChange}
          />
          <input
            style={input}
            name="state"
            placeholder="State"
            value={form.state}
            onChange={handleChange}
          />
        </div>
        <input
          style={input}
          name="postcode"
          placeholder="Postcode"
          value={form.postcode}
          onChange={handleChange}
        />

        {/* Sale */}
        <div style={section}>Sale</div>
        <input
          style={input}
          name="price"
          placeholder="Sale Price ($)"
          value={form.price}
          onChange={handleChange}
        />

        <label style={{ display: "block", marginTop: 6 }}>
          <input
            type="checkbox"
            checked={form.enquiry_only === "1"}
            onChange={(e) =>
              setForm({
                ...form,
                enquiry_only: e.target.checked ? "1" : "0",
              })
            }
          />{" "}
          Enquiry Only
        </label>

        <button
          disabled={saving}
          style={{
            width: "100%",
            padding: 14,
            marginTop: 18,
            borderRadius: 8,
            border: "none",
            background: saving ? "#9e9e9e" : "#2e7d32",
            color: "#fff",
            fontWeight: 800,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>

      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          width: "100%",
          padding: 12,
          marginTop: 10,
          borderRadius: 8,
          border: "1px solid #ccc",
          background: "#f5f5f5",
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        Back
      </button>
    </div>
  );
}
